import React, { FC, Fragment, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import moment from 'moment';

import { ReachStat, StatsGrouping } from 'api';
import { Charting, CustomSVG, Numeric, Queries, SocialAccountIcon } from 'components';
import {
  getReachByProvider,
  getTotalPreviousReach,
  getTotalReach,
  IReachByProvider,
} from 'helpers';

interface IProps {
  emptyState: () => ReactNode;
}

const useStyles = makeStyles(theme => ({
  pieText: {
    position: 'absolute',
    top: '40%',
    left: '50%',
    transform: 'translateY(-40%) translateX(-50%)',
    textAlign: 'center',
  },
  legend: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: theme.spacing(2),
  },
  legendText: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: 12,
  },
  previous: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(1),
  },
}));

const MyReach: FC<IProps> = ({ emptyState }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const [variables, setVariables] = useState({
    grouping: StatsGrouping.Year,
    value: moment().year(),
  });

  function getColor(provider: string) {
    return theme.custom.networks[provider];
  }

  function getLabel(current: boolean) {
    const { grouping } = variables;

    switch (grouping) {
      case StatsGrouping.Week:
        return t(current ? 'components:thisWeek' : 'components:lastWeek');
      case StatsGrouping.Month:
        return t(current ? 'components:thisMonth' : 'components:lastMonth');
      case StatsGrouping.Year:
        return t(current ? 'components:thisYear' : 'components:lastYear');
    }
  }

  function renderPrevious(reachStats: ReachStat[]) {
    const previousReach = getTotalPreviousReach(reachStats);
    const previousLabel = getLabel(false);

    return (
      <div className={classes.previous}>
        <Typography align="right">{previousLabel}:</Typography>
        <Typography align="right">
          <Numeric value={previousReach} />
        </Typography>
      </div>
    );
  }

  function getIcon(entry: IReachByProvider) {
    return entry.provider === 'Company' ? (
      <CustomSVG name="everyoneSocial" className={classes.icon} />
    ) : (
      <SocialAccountIcon active provider={entry.provider as any} />
    );
  }

  function renderLegend(reachStats: ReachStat[]) {
    const reachByProvider = getReachByProvider(reachStats);

    return (
      <div className={classes.legend}>
        <Grid container alignItems="center" justify="space-around">
          {reachByProvider.map(entry => (
            <Grid item key={entry.provider}>
              <Typography variant="subtitle2" className={classes.legendText}>
                {getIcon(entry)}
                <Numeric value={entry.reach} />
              </Typography>
            </Grid>
          ))}
        </Grid>
      </div>
    );
  }

  function renderChart(reachStats: ReachStat[], data: IReachByProvider[]) {
    return (
      <Fragment>
        {renderPrevious(reachStats)}
        <ResponsiveContainer width="100%" height="100%">
          <PieChart data={data}>
            <Pie
              data={data}
              dataKey="reach"
              nameKey="provider"
              cy="40%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={2}
            >
              {data.map((datum: IReachByProvider) => (
                <Cell key={datum.provider} fill={getColor(datum.provider)} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className={classes.pieText}>
          <Typography>{getLabel(true)}</Typography>
          <Typography color="inherit" variant="h4">
            <Numeric value={getTotalReach(reachStats)} />
          </Typography>
        </div>
        {renderLegend(reachStats)}
      </Fragment>
    );
  }

  return (
    <Charting.Widget title={t('user:myReach')} variables={variables} onChange={setVariables}>
      <Queries.UserReach {...variables}>
        {reachStats => {
          const data = getReachByProvider(reachStats);
          return data.length ? renderChart(reachStats, data) : emptyState();
        }}
      </Queries.UserReach>
    </Charting.Widget>
  );
};

export default MyReach;
